import type { FC } from 'react';
import {
  Link as WouterLink,
  type LinkProps as WouterLinkProps,
  useLocation,
  useRouter,
} from 'wouter';

import { useLanguage } from 'store';

type LinkProps = {
  activeStrategy?: 'exact' | 'nested';
} & WouterLinkProps;

const isLocationNested = (
  location: string,
  targetPath: string,
  basePath: string,
): boolean => {
  const isAbsolute = targetPath[0] === '~';
  const target = isAbsolute ? targetPath.slice(1) : targetPath;
  const fullLocation = isAbsolute ? `${basePath}${location}` : location;

  return fullLocation.startsWith(target);
};

const Link: FC<LinkProps> = ({
  activeStrategy = 'exact',
  href,
  to,
  ...props
}) => {
  const [{ basePath }] = useLanguage();
  const router = useRouter();
  const [location] = useLocation();
  let target = href ?? to;
  const isAbsolute = target[0] === '~';
  const isExternal =
    target.startsWith('https://') || target.startsWith('http://');
  if (isAbsolute && basePath) {
    target = target.replace('~', `~${basePath}`);
  }

  // className does not work with asChild
  if (props.asChild) {
    return <WouterLink {...props} href={target} />;
  }

  if (isExternal) {
    const className =
      typeof props.className === 'function'
        ? props.className(false) // external links are never active
        : props.className;

    return <a {...props} className={className} href={target} />;
  }

  let className = props.className;

  if (typeof className === 'function' && activeStrategy === 'nested') {
    const isActive = isLocationNested(location, target, router.base);
    className = className(isActive);
  }

  return <WouterLink {...props} className={className} href={target} />;
};

export default Link;
